// utils/membersConfig.js

export const MEMBER_TYPES = {
  HOUSE: 'house',
  SENATE: 'senate',
  PRESIDENT: 'president',
  VICE_PRESIDENT: 'vice_president',
  JUSTICE: 'justice',
};

export const membersConfig = {
  [MEMBER_TYPES.HOUSE]: {
    name: 'House of Representatives',
    apiEndpoint: 'https://api.govglance.org/members/',
    filterField: 'chamber',
    filterValue: 'House of Representatives',
    listRoute: '/us/houseofreps',
    detailRoute: '/us/representative',
    dataType: 'congress', // Uses first_name, last_name, bio_guide_id, depication.imageUrl
  },
  [MEMBER_TYPES.SENATE]: {
    name: 'Senate',
    apiEndpoint: 'https://api.govglance.org/members/',
    filterField: 'chamber',
    filterValue: 'Senate',
    listRoute: '/us/senators',
    detailRoute: '/us/senator',
    dataType: 'congress',
  },
  [MEMBER_TYPES.PRESIDENT]: {
    name: 'Presidents',
    apiEndpoint: 'https://api.govglance.org/members/presidents',
    orderBy: 'president_num',
    listRoute: '/us/presidents',
    detailRoute: '/us/president',
    dataType: 'executive', // Uses full_name, url, president_num, description
  },
  [MEMBER_TYPES.VICE_PRESIDENT]: {
    name: 'Vice Presidents',
    apiEndpoint: 'https://api.govglance.org/members/vice_presidents',
    orderBy: 'id',
    listRoute: '/us/vicepresidents',
    detailRoute: '/us/vicepresident',
    dataType: 'executive',
  },
  [MEMBER_TYPES.JUSTICE]: {
    name: 'Supreme Court Justices',
    apiEndpoint: 'https://api.govglance.org/members/justices',
    listRoute: '/us/justices',
    detailRoute: '/us/justice',
    dataType: 'judicial', // Uses full_name, url, description
  },
};

export const getMemberConfig = (memberType) => {
  return membersConfig[memberType] || membersConfig[MEMBER_TYPES.HOUSE];
};

export const getMemberConfigByChamber = (chamber) => {
  if (chamber === 'Senate') return membersConfig[MEMBER_TYPES.SENATE];
  if (chamber === 'House of Representatives') return membersConfig[MEMBER_TYPES.HOUSE];
  if (chamber === 'President') return membersConfig[MEMBER_TYPES.PRESIDENT];
  if (chamber === 'Supreme Court') return membersConfig[MEMBER_TYPES.JUSTICE];
  return membersConfig[MEMBER_TYPES.HOUSE];
};

export const getOrdinalSuffix = (number) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = number % 100;
  return number + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

export const formatDate = (inputDate) => {
  const date = new Date(inputDate);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

/**
 * Extract and normalize member data from raw API response
 * Handles both congress (House/Senate) and executive/judicial (President/Justice) formats
 */
export const extractMemberData = (rawMember, memberType) => {
  if (!rawMember) return null;

  const config = getMemberConfig(memberType);

  // Handle President/Justice format (executive/judicial dataType)
  if (config.dataType === 'executive' || config.dataType === 'judicial') {
    return {
      // Use a unique identifier - for presidents use president_num, for justices use full_name hash
      bioGuideId: rawMember.president_num?.toString() || rawMember.full_name?.replace(/\s+/g, '-').toLowerCase(),
      firstName: rawMember.full_name?.split(' ')[0] || '',
      lastName: rawMember.full_name?.split(' ').slice(1).join(' ') || '',
      fullName: rawMember.full_name,
      imageUrl: rawMember.url,
      websiteUrl: null,
      chamber: config.dataType === 'executive' ? 'President' : 'Supreme Court',
      startYear: rawMember.term_start_date ? new Date(rawMember.term_start_date).getFullYear() : null,
      termStartDate: rawMember.term_start_date,
      termEndDate: rawMember.term_end_date,
      stateName: null,
      district: null,
      partyHistory: null,
      addressInfo: null,
      description: rawMember.description,
      presidentNumber: rawMember.president_num,
    };
  }

  // Handle Congress format (House/Senate)
  const latestTerm = rawMember.terms?.[rawMember.terms.length - 1] || {};

  return {
    bioGuideId: rawMember.bio_guide_id,
    firstName: rawMember.first_name,
    lastName: rawMember.last_name,
    fullName: `${rawMember.first_name} ${rawMember.last_name}`,
    imageUrl: rawMember.depication?.imageUrl,
    websiteUrl: rawMember.official_website_url,
    chamber: latestTerm.chamber,
    startYear: latestTerm.startYear,
    termStartDate: null,
    termEndDate: null,
    stateName: latestTerm.stateName,
    district: latestTerm.district,
    partyHistory: rawMember.party_history,
    addressInfo: rawMember.address_information,
    description: null,
    presidentNumber: null,
  };
};